import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Loader2, Pencil, Trash2, Save } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fetcher = (url) => api.get(url).then((r) => r.data);

const emptyCompany = {
  name: "", company_number: "", vat_number: "", registered_address: "",
  currency: "GBP", timezone: "Europe/London",
  vat_percentage: 20, flat_rate_vat_percentage: 10,
  invoice_prefix: "", bank_name: "", account_name: "",
  sort_code: "", account_number: "",
};

const Companies = () => {
  const { data, isLoading } = useSWR("/companies", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCompany);
  const [saving, setSaving] = useState(false);

  // Edit + Delete
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({ ...emptyCompany, ...c });
  };
  const setE = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name) return toast.error("Company name is required");
    setEditSaving(true);
    try {
      await api.patch(`/companies/${editing.id}`, {
        ...editForm,
        vat_percentage: parseFloat(editForm.vat_percentage) || 0,
        flat_rate_vat_percentage: parseFloat(editForm.flat_rate_vat_percentage) || 0,
      });
      toast.success(`Company '${editForm.name}' updated`);
      setEditing(null);
      mutate("/companies");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
    setEditSaving(false);
  };
  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/companies/${deleting.id}`);
      toast.success(`Company '${deleting.name}' deleted`);
      setDeleting(null);
      mutate("/companies");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Company name is required");
    setSaving(true);
    try {
      await api.post("/companies", {
        ...form,
        vat_percentage: parseFloat(form.vat_percentage),
        flat_rate_vat_percentage: parseFloat(form.flat_rate_vat_percentage),
      });
      toast.success(`Company '${form.name}' created`);
      setOpen(false);
      setForm(emptyCompany);
      mutate("/companies");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Platform · tenants</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Companies</h1>
          <p className="text-sm text-brand-text-muted mt-1">
            {data?.length || 0} tenant{(data?.length || 0) === 1 ? "" : "s"} on the platform.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="company-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Onboard a new company</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Company name *</Label>
                  <Input required data-testid="company-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Company number</Label>
                  <Input value={form.company_number} onChange={(e) => set("company_number", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">VAT number</Label>
                  <Input value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} /></div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Registered address</Label>
                  <Input value={form.registered_address} onChange={(e) => set("registered_address", e.target.value)} />
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Currency</Label>
                  <Input value={form.currency} onChange={(e) => set("currency", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Timezone</Label>
                  <Input value={form.timezone} onChange={(e) => set("timezone", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Standard VAT %</Label>
                  <Input type="number" value={form.vat_percentage} onChange={(e) => set("vat_percentage", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Flat rate VAT %</Label>
                  <Input type="number" value={form.flat_rate_vat_percentage} onChange={(e) => set("flat_rate_vat_percentage", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Invoice prefix</Label>
                  <Input value={form.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value)} placeholder="e.g. MCT" /></div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="bg-brand-red hover:bg-brand-red-hover" data-testid="company-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                  Create company
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="text-sm text-brand-text-muted">Loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="companies-grid">
        {(data || []).map((c) => (
          <Card key={c.id} className="p-5 rounded-2xl border-brand-border" data-testid={`company-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-brand-navy text-white grid place-items-center overflow-hidden">
                  {c.logo_url
                    ? <img src={c.logo_url} alt={c.name} className="w-full h-full object-cover" />
                    : <Building2 className="w-5 h-5" />
                  }
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-brand-text truncate">{c.name}</div>
                  <div className="text-[11px] text-brand-text-muted mt-0.5">{c.company_number || "no company no."}</div>
                </div>
              </div>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{c.status || "active"}</Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
              <div><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">VAT</div>
                <div className="mt-0.5 text-brand-text font-mono">{c.vat_number || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Currency</div>
                <div className="mt-0.5 text-brand-text">{c.currency} · {c.timezone}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Invoice prefix</div>
                <div className="mt-0.5 text-brand-text font-mono">{c.invoice_prefix || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Onboarded</div>
                <div className="mt-0.5 text-brand-text font-mono">{ddmmyyyy(c.created_at)}</div></div>
            </div>
            <div className="mt-4 pt-3 border-t border-brand-border flex items-center justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`company-edit-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleting(c)} data-testid={`company-del-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-600" />
              </Button>
            </div>
          </Card>
        ))}
        {!isLoading && (data || []).length === 0 && (
          <div className="col-span-full text-center py-12 text-brand-text-muted text-sm">
            <Building2 className="w-6 h-6 mx-auto mb-2 opacity-40" />No companies yet.
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="company-edit-dialog">
          <DialogHeader><DialogTitle>Edit {editing?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Company name *</Label>
                <Input required data-testid="company-edit-name" value={editForm.name || ""} onChange={(e) => setE("name", e.target.value)} />
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Company number</Label>
                <Input value={editForm.company_number || ""} onChange={(e) => setE("company_number", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">VAT number</Label>
                <Input value={editForm.vat_number || ""} onChange={(e) => setE("vat_number", e.target.value)} /></div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Registered address</Label>
                <Input value={editForm.registered_address || ""} onChange={(e) => setE("registered_address", e.target.value)} />
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Currency</Label>
                <Input value={editForm.currency || ""} onChange={(e) => setE("currency", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Timezone</Label>
                <Input value={editForm.timezone || ""} onChange={(e) => setE("timezone", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Standard VAT %</Label>
                <Input type="number" value={editForm.vat_percentage ?? ""} onChange={(e) => setE("vat_percentage", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Flat rate VAT %</Label>
                <Input type="number" value={editForm.flat_rate_vat_percentage ?? ""} onChange={(e) => setE("flat_rate_vat_percentage", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-brand-text-muted">Invoice prefix</Label>
                <Input value={editForm.invoice_prefix || ""} onChange={(e) => setE("invoice_prefix", e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={editSaving} className="bg-brand-red hover:bg-brand-red-hover" data-testid="company-edit-save">
                {editSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="company-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The company will be marked as deleted and removed from the list. Historical data (users, drivers, invoices) is preserved for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700"
                                data-testid="company-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Companies;
